import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, CheckCircle2, XCircle, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { runChunkedBulkDelete, BATCH_SIZE } from "@/components/bulk/runChunkedBulkDelete";

/**
 * Permanent Test-Data Purge modal.
 *
 * Two-step flow, deliberately more friction than the soft-delete modal:
 *   1. Preview — server-computed impact summary of what will be
 *      destroyed. Loaded once when the modal opens.
 *   2. Confirmation — admin must type "PURGE TEST DATA" verbatim
 *      before the primary button unlocks.
 *
 * The endpoint accepts ≤200 ids per request. Selections > 200 are
 * chunked sequentially via the shared runner, with a shared
 * `bulk_operation_id` stamped on every server-side audit entry.
 */
const CONFIRMATION_PHRASE = "PURGE TEST DATA";

// Friendly labels for the per-collection counts returned by the server.
const COLLECTION_LABELS = {
    "projects.customer_id": "Projects (as customer)",
    "projects.customer_email": "Projects (by email)",
    "tasks.assigned_user_id": "Tasks (assigned)",
    "tasks.assigned_engineer_id": "Tasks (as engineer)",
    "tasks.created_by": "Tasks (created)",
    "appointments.engineer_id": "Appointments (as engineer)",
    "appointments.engineer_ids": "Appointments (engineer roster)",
    "appointments.created_by": "Appointments (created)",
    "documents.uploaded_by_id": "Documents uploaded",
    "notifications.recipient_id": "Notifications received",
    "notifications.engineer_id": "Notifications (as engineer)",
    "messages.sender_id": "Messages sent",
    "sessions.user_id": "Active sessions",
    "password_reset_tokens.user_id": "Password-reset tokens",
    "audit_logs.actorId": "Project-timeline events (as actor)",
    "security_audit.user_id": "Security audit rows (as subject)",
    "security_audit.admin_id": "Security audit rows (as admin)",
    "journeys.user_id": "Journey progress records",
    "engineers.email": "Engineer records",
};


export default function PermanentPurgeModal({
    open,
    userIds,
    onCancel,
    onDone,
    testidPrefix = "purge",
}) {
    const [phase, setPhase] = useState("loading");   // loading | review | running | done | stopped | error
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState("");
    const [typedConfirm, setTypedConfirm] = useState("");
    const [progress, setProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [runError, setRunError] = useState("");

    useEffect(() => {
        if (!open) return;
        setPhase("loading");
        setPreview(null);
        setPreviewError("");
        setTypedConfirm("");
        setProgress(null);
        setResult(null);
        setRunError("");
        (async () => {
            try {
                const p = await api.adminPurgePreview(userIds);
                setPreview(p);
                setPhase("review");
            } catch (e) {
                setPreviewError(formatApiError(e?.response?.data?.detail) || "Preview failed");
                setPhase("error");
            }
        })();
    }, [open, userIds]);

    const eligibleIds = useMemo(
        () => (preview?.users || []).filter((u) => u.eligible).map((u) => u.id),
        [preview],
    );
    const totalBatches = Math.max(1, Math.ceil(eligibleIds.length / BATCH_SIZE));
    const canConfirm = phase === "review"
        && typedConfirm === CONFIRMATION_PHRASE
        && eligibleIds.length > 0;

    async function runPurge() {
        setPhase("running");
        setRunError("");
        try {
            const agg = await runChunkedBulkDelete(
                eligibleIds,
                (batch, ctx) => api.adminPermanentPurge(batch, CONFIRMATION_PHRASE, ctx)
                    .then((r) => ({
                        // Normalise the shape so runChunkedBulkDelete can
                        // aggregate consistently with the soft-delete API.
                        requested: r.requested,
                        deleted: r.purged,
                        failed: r.failed,
                        deleted_ids: r.deleted_ids,
                        failed_ids: r.failed_ids,
                        failures: r.failures,
                        deleted_counts: r.deleted_counts,
                        unlinked_counts: r.unlinked_counts,
                    })),
                (p) => setProgress(p),
            );
            // Sum up deleted_counts / unlinked_counts across batches.
            const dc = {}; const uc = {};
            for (const b of agg.batches) {
                const r = b.result || {};
                for (const [k, v] of Object.entries(r.deleted_counts || {})) dc[k] = (dc[k] || 0) + v;
                for (const [k, v] of Object.entries(r.unlinked_counts || {})) uc[k] = (uc[k] || 0) + v;
            }
            agg.deleted_counts = dc;
            agg.unlinked_counts = uc;
            setResult(agg);
            setPhase(agg.stopped ? "stopped" : "done");
        } catch (e) {
            setRunError(e?.message || "Purge failed");
            setPhase("error");
        }
    }

    function close() {
        if (phase === "done" || phase === "stopped") onDone?.(result);
        onCancel?.();
    }

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-3"
            role="dialog"
            aria-modal="true"
            data-testid={`${testidPrefix}-modal`}
            onKeyDown={(e) => {
                if (e.key === "Escape" && (phase === "review" || phase === "done" ||
                                            phase === "stopped" || phase === "error")) close();
            }}
        >
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden">
                <div className="p-5 md:p-6 border-b border-[#E5E7EB] flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#FEE2E2] flex items-center justify-center shrink-0">
                        <ShieldAlert size={18} className="text-[#B91C1C]" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-base font-medium text-[#131B2F]">
                            Permanent test-data purge
                        </div>
                        <p className="mt-0.5 text-sm text-[#4B5565]">
                            Wipes {userIds.length} user{userIds.length === 1 ? "" : "s"} and every record whose sole subject is that user. Shared records are retained with the user's association removed.
                        </p>
                        {preview && (
                            <p className="mt-1 text-xs text-[#4B5565]">
                                Environment:{" "}
                                <span className="font-mono px-1.5 py-0.5 rounded bg-[#F5F5F7]" data-testid={`${testidPrefix}-env`}>
                                    {preview.environment}
                                </span>
                            </p>
                        )}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 md:p-6">
                    {phase === "loading" && (
                        <div className="py-8 flex items-center gap-3 text-[#4B5565]">
                            <Loader2 size={16} className="animate-spin" />
                            Computing impact…
                        </div>
                    )}

                    {phase === "error" && (
                        <div className="text-sm text-[#B91C1C]" data-testid={`${testidPrefix}-error`}>
                            {previewError || runError || "Something went wrong."}
                        </div>
                    )}

                    {phase === "review" && preview && (
                        <ReviewPanel
                            preview={preview}
                            totalBatches={totalBatches}
                            typedConfirm={typedConfirm}
                            onTypedConfirm={setTypedConfirm}
                            testidPrefix={testidPrefix}
                        />
                    )}

                    {phase === "running" && progress && (
                        <RunningPanel progress={progress} testidPrefix={testidPrefix} />
                    )}

                    {(phase === "done" || phase === "stopped") && result && (
                        <ResultPanel
                            result={result}
                            stopped={phase === "stopped"}
                            testidPrefix={testidPrefix}
                        />
                    )}
                </div>

                <div className="px-5 md:px-6 py-3 bg-[#F9FAFB] border-t border-[#E5E7EB] flex justify-between items-center gap-2">
                    <div className="text-[11px] text-[#4B5565]">
                        {phase === "review" && preview
                            ? `${preview.eligible_count} eligible · ${preview.ineligible_count} ineligible · ${preview.not_found_count} not found`
                            : phase === "done" || phase === "stopped"
                                ? "Operation complete."
                                : ""}
                    </div>
                    <div className="flex gap-2">
                        {(phase === "review" || phase === "error") && (
                            <button
                                type="button"
                                onClick={close}
                                className="h-10 px-4 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] hover:bg-[#F5F5F7]"
                                data-testid={`${testidPrefix}-cancel`}
                            >
                                Cancel
                            </button>
                        )}
                        {phase === "review" && (
                            <button
                                type="button"
                                onClick={runPurge}
                                disabled={!canConfirm}
                                className="h-10 px-4 rounded-md bg-[#B91C1C] hover:bg-[#991B1B] text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid={`${testidPrefix}-confirm`}
                            >
                                Purge {eligibleIds.length} user{eligibleIds.length === 1 ? "" : "s"}
                            </button>
                        )}
                        {(phase === "done" || phase === "stopped") && (
                            <button
                                type="button"
                                onClick={close}
                                className="h-10 px-4 rounded-md bg-[#131B2F] hover:bg-[#0B1220] text-white text-sm font-medium"
                                data-testid={`${testidPrefix}-close`}
                            >
                                Close
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}


function ReviewPanel({ preview, totalBatches, typedConfirm, onTypedConfirm, testidPrefix }) {
    const totals = preview.totals || {};
    const rows = Object.entries(totals)
        .filter(([, v]) => v > 0)
        .sort((a, b) => b[1] - a[1]);
    const noRefs = rows.length === 0;

    return (
        <div>
            <div className="flex items-start gap-2 rounded-md bg-[#FEF3C7] px-3 py-2 mb-4">
                <AlertTriangle size={14} className="text-[#B45309] mt-0.5 shrink-0" />
                <div className="text-xs text-[#7C2D12]">
                    This action is <span className="font-medium">permanent and irreversible</span>. There is no soft-delete tombstone — every referenced record listed below will be destroyed or detached.
                </div>
            </div>

            {preview.production && (
                <div
                    className="mb-4 rounded-md bg-[#FEE2E2] border border-[#FCA5A5] px-3 py-2 text-xs text-[#7F1D1D]"
                    data-testid={`${testidPrefix}-prod-warning`}
                >
                    You are on a <strong>production</strong> environment. Only users explicitly flagged as{" "}
                    <span className="font-mono">is_test_data</span> can be purged here.
                </div>
            )}

            <div className="text-[11px] uppercase tracking-[0.08em] text-[#4B5565] font-medium mb-2">
                Users in this batch
            </div>
            <div className="border border-[#E5E7EB] rounded-md overflow-hidden mb-5">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F5F7] text-[11px] uppercase text-[#4B5565]">
                        <tr>
                            <th className="text-left px-3 py-2">User</th>
                            <th className="text-left px-3 py-2">Role</th>
                            <th className="text-left px-3 py-2">Test flag</th>
                            <th className="text-left px-3 py-2">Eligible</th>
                        </tr>
                    </thead>
                    <tbody>
                        {preview.users.map((u) => (
                            <tr
                                key={u.id}
                                className="border-t border-[#F3F4F6]"
                                data-testid={`${testidPrefix}-user-row-${u.id}`}
                            >
                                <td className="px-3 py-2 text-[#131B2F]">
                                    {u.found ? (u.name || u.email || u.id) : (
                                        <span className="text-[#B91C1C]">not found</span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-[#4B5565]">{u.role || "—"}</td>
                                <td className="px-3 py-2">
                                    {u.is_test_data
                                        ? <span className="text-[#2E7E22]">yes</span>
                                        : <span className="text-[#4B5565]">no</span>}
                                </td>
                                <td className="px-3 py-2">
                                    {u.eligible
                                        ? <span className="text-[#2E7E22]">yes</span>
                                        : <span className="text-[#B91C1C]" title={u.reason}>no — {u.reason}</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="text-[11px] uppercase tracking-[0.08em] text-[#4B5565] font-medium mb-2">
                Records referenced across the batch
            </div>
            {noRefs ? (
                <div className="text-sm text-[#4B5565] py-2">No associated records found for these users.</div>
            ) : (
                <div className="border border-[#E5E7EB] rounded-md overflow-hidden mb-5" data-testid={`${testidPrefix}-totals`}>
                    <table className="w-full text-sm">
                        <tbody>
                            {rows.map(([k, v]) => (
                                <tr key={k} className="border-t border-[#F3F4F6] first:border-t-0">
                                    <td className="px-3 py-1.5 text-[#4B5565]">{COLLECTION_LABELS[k] || k}</td>
                                    <td className="px-3 py-1.5 text-right font-medium text-[#131B2F]" data-testid={`${testidPrefix}-total-${k}`}>
                                        {v.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {totalBatches > 1 && (
                <div className="text-xs text-[#4B5565] bg-[#F5F5F7] rounded px-2 py-1.5 mb-5">
                    Batching: eligible users will be processed in <span className="font-medium">{totalBatches} batches</span> of up to {BATCH_SIZE} each. If a batch fails, subsequent batches are skipped and no automatic retry runs.
                </div>
            )}

            <div>
                <label className="block text-sm text-[#4B5565] mb-1.5">
                    Type <span className="font-mono font-medium text-[#131B2F]">{CONFIRMATION_PHRASE}</span> to enable the purge button.
                </label>
                <input
                    type="text"
                    value={typedConfirm}
                    onChange={(e) => onTypedConfirm(e.target.value)}
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                    placeholder={CONFIRMATION_PHRASE}
                    className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base text-[#131B2F] font-mono outline-none focus:border-[#B91C1C]"
                    data-testid={`${testidPrefix}-confirm-input`}
                />
            </div>
        </div>
    );
}


function RunningPanel({ progress, testidPrefix }) {
    return (
        <div data-testid={`${testidPrefix}-progress`}>
            <div className="flex items-center gap-3">
                <Loader2 size={18} className="text-[#131B2F] animate-spin" />
                <div>
                    <div className="text-base font-medium text-[#131B2F]">
                        Purging batch {progress.batchNumber} of {progress.totalBatches}
                    </div>
                    <div className="text-sm text-[#4B5565] mt-0.5" data-testid={`${testidPrefix}-progress-line`}>
                        {progress.deletedSoFar} of {progress.total} users purged
                    </div>
                </div>
            </div>
            <div className="mt-4 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                <div
                    className="h-full bg-[#B91C1C] transition-all"
                    style={{ width: `${Math.min(100, (progress.deletedSoFar / Math.max(1, progress.total)) * 100)}%` }}
                />
            </div>
            <p className="mt-3 text-xs text-[#4B5565]">
                Please leave this window open until the summary appears.
            </p>
        </div>
    );
}


function ResultPanel({ result, stopped, testidPrefix }) {
    const dc = result.deleted_counts || {};
    const uc = result.unlinked_counts || {};
    const dcRows = Object.entries(dc).sort((a, b) => b[1] - a[1]);
    const ucRows = Object.entries(uc).sort((a, b) => b[1] - a[1]);
    return (
        <div data-testid={`${testidPrefix}-summary`}>
            <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${stopped ? "bg-[#FEE2E2]" : "bg-[#E6F1DE]"}`}>
                    {stopped
                        ? <XCircle size={18} className="text-[#B91C1C]" />
                        : <CheckCircle2 size={18} className="text-[#2E7E22]" />}
                </div>
                <div>
                    <div className="text-base font-medium text-[#131B2F]">
                        {stopped
                            ? `Stopped at batch ${result.failedBatch?.number || "?"}`
                            : `Purged ${result.deleted} of ${result.total} users`}
                    </div>
                    <dl className="mt-2 text-sm grid grid-cols-2 gap-x-4 gap-y-1">
                        <SummaryRow label="Total selected" value={result.total} />
                        <SummaryRow label="Purged" value={result.deleted} tone="ok" />
                        <SummaryRow label="Failed" value={result.failed} tone={result.failed ? "warn" : "muted"} />
                        {result.skipped > 0 && (
                            <SummaryRow label="Skipped" value={result.skipped} tone="warn" />
                        )}
                    </dl>
                </div>
            </div>

            {stopped && result.failedBatch && (
                <div className="mt-4 rounded-md bg-[#FEE2E2] px-3 py-2 text-xs text-[#7F1D1D]" data-testid={`${testidPrefix}-batch-error`}>
                    Batch {result.failedBatch.number} failed ({result.failedBatch.size} users). Reason: {result.failedBatch.error}. {result.deleted} users purged before the stop; {result.skipped} untouched. Nothing was retried automatically.
                </div>
            )}

            {dcRows.length > 0 && (
                <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-[0.08em] text-[#4B5565] font-medium mb-2">
                        Records deleted
                    </div>
                    <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <tbody>
                                {dcRows.map(([k, v]) => (
                                    <tr key={k} className="border-t border-[#F3F4F6] first:border-t-0">
                                        <td className="px-3 py-1.5 text-[#4B5565]">{COLLECTION_LABELS[k] || k}</td>
                                        <td className="px-3 py-1.5 text-right font-medium text-[#131B2F]" data-testid={`${testidPrefix}-deleted-${k}`}>
                                            {v.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {ucRows.length > 0 && (
                <div className="mt-5">
                    <div className="text-[11px] uppercase tracking-[0.08em] text-[#4B5565] font-medium mb-2">
                        Records retained with user's link removed
                    </div>
                    <div className="border border-[#E5E7EB] rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                            <tbody>
                                {ucRows.map(([k, v]) => (
                                    <tr key={k} className="border-t border-[#F3F4F6] first:border-t-0">
                                        <td className="px-3 py-1.5 text-[#4B5565]">{COLLECTION_LABELS[k] || k}</td>
                                        <td className="px-3 py-1.5 text-right font-medium text-[#131B2F]" data-testid={`${testidPrefix}-unlinked-${k}`}>
                                            {v.toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}


function SummaryRow({ label, value, tone = "muted" }) {
    const tones = {
        ok: "text-[#2E7E22]",
        warn: "text-[#B45309]",
        muted: "text-[#131B2F]",
    };
    return (
        <>
            <dt className="text-[#4B5565]">{label}</dt>
            <dd className={`font-medium text-right ${tones[tone] || tones.muted}`}>{value}</dd>
        </>
    );
}
